import { ThemeEvents, VariantUpdateEvent, CartUpdateEvent, CartAddEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { morph } from '@theme/morph';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';

/**
 * Line item properties for the JSON /cart/add paths.
 *
 * These build their body in JS rather than serialising a form, so they miss the hidden inputs
 * product-form-item-source renders. `_auctionCode` is the only thing AMS routes on - without it a
 * missed-auction item added from a card is attributed to FPC instead of Missed - so it is carried
 * per-variant on data-auction-code and picked up here.
 *
 * An empty code means a plain FPC line and sends no property at all. Never send an empty
 * `_auctionCode`: the property would still exist on the line, and the middleware SP distinguishes
 * present from absent. Liquid leaves the attribute empty both when the variant has no code and when
 * missed-auction-code suppresses it because the item is currently on air.
 *
 * @param {Element} trigger - the clicked element, used to resolve Rebuy slider attribution
 * @param {string | null | undefined} auctionCode - data-auction-code for the variant being added
 * @returns {Record<string, string | null>}
 */
function buildQuickAddProperties(trigger, auctionCode) {
  const code = (auctionCode ?? '').trim();

  /** @type {Record<string, string | null>} */
  const properties = { _itemSource: code ? 'MA' : 'FPC' };
  if (code) properties._auctionCode = code;

  const rebuySlider = trigger.closest('.rebuy-product-slider');
  if (rebuySlider) {
    properties._source = rebuySlider.getAttribute('data-product-item-source');
    properties._widget_id = rebuySlider.getAttribute('data-product-item-widget_id');
    properties._attribution = rebuySlider.getAttribute('data-product-item-attribution');
  }

  return properties;
}

/**
 * A custom element that handles quick add functionality for product cards.
 * This component listens for clicks on quick-add buttons and size options,
 * and handles variant selection updates to update available sizes.
 * Similar in structure to product-price.js but with cart add functionality.
 */
export class QuickAdd extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();

  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog, product-card');
    if (!closestSection) return;

    // Listen for variant selection updates
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantSelected);
    
    // Handle size option clicks
    this.addEventListener('click', this.#handleSizeOptionClick);
    
    // Handle simple Quick Add button clicks
    this.addEventListener('click', this.#handleQuickAddButtonClick);
    this.addEventListener('click', this.#handleMobileQuickAddButtonClick);
    this.addEventListener('pointerdown', this.#syncKlipVariant, true);

    // Listen for media query changes to close modal
    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog, product-card');
    if (!closestSection) return;

    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantSelected);
    this.removeEventListener('click', this.#handleSizeOptionClick);
    this.removeEventListener('click', this.#handleQuickAddButtonClick);
    this.removeEventListener('click', this.#handleMobileQuickAddButtonClick);
    this.removeEventListener('pointerdown', this.#syncKlipVariant, true);
    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    
    this.#abortController?.abort();
  }

  /**
   * Handles size option clicks for quick add
   * @param {Event} event - The click event
   */
  #handleSizeOptionClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const sizeOption = event.target.closest('.product-card__size-option');
    if (!(sizeOption instanceof HTMLButtonElement) || sizeOption.disabled || !sizeOption.hasAttribute('data-variant-id')) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const variantId = sizeOption.getAttribute('data-variant-id');
    if (!variantId) return;

    sizeOption.disabled = true;
    sizeOption.classList.add('product-card__size-option--loading');
    sizeOption.setAttribute('aria-busy', 'true');

    const productId = sizeOption.getAttribute('data-product-id');
    const productTitle = sizeOption.getAttribute('data-product-title');
    const productVariant = sizeOption.getAttribute('data-product-variant');
    const productPrice = sizeOption.getAttribute('data-product-price');

    // Read off the clicked size button, not the host element: each size is a different variant and
    // carries its own code.
    const properties = buildQuickAddProperties(event.target, sizeOption.getAttribute('data-auction-code'));

    // Add to cart via AJAX
    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items: [{
          id: variantId,
          quantity: 1,
          properties: properties
        }]
      })
    })
    .then(async (response) => {
      const data = await response.json().catch(() => ({}));
      const ok = response.ok && !(data && typeof data === 'object' && data.status);
      return { ok, data };
    })
    .then(async ({ ok }) => {
      if (!ok) {
        sizeOption.disabled = false;
        return;
      }

      this.dispatchEvent(new CartUpdateEvent({}, this.id, {
        action: 'add',
        source: 'quick-add',
        productId: this.dataset.productId,
        productData: {
          masterProductId: this.dataset.productMasterId,
          item_id: productId || this.dataset.productId,
          item_name: productTitle || this.dataset.productTitle,
          item_brand: this.dataset.productVendor,
          item_category: this.dataset.productCategory,
          item_variant: productVariant || this.dataset.productVariant,
          price: productPrice || this.dataset.productPrice,
          quantity: 1,
        }
      }));
    })
    .catch((error) => {
      console.error('Error adding to cart:', error);
    })
    .finally(() => {
      sizeOption.classList.remove('product-card__size-option--loading');
      sizeOption.setAttribute('aria-busy', 'false');
      sizeOption.disabled = false;
    });
  };


  /**
   * Points the Klip form's hidden variant input at the variant the shopper is about to add.
   * The Klip coupons app resolves coupons from that input, and it reads it before the cart
   * request goes out, so the sync has to happen on pointer down rather than on click.
   * @param {Event} event - The pointerdown event
   */
  #syncKlipVariant = (event) => {
    if (!(event.target instanceof Element)) return;

    const variantId = event.target.closest('[data-variant-id]')?.getAttribute('data-variant-id');
    if (!variantId) return;

    const variantInput = this.querySelector('.klip-quick-add-form input[name="id"]');
    if (variantInput instanceof HTMLInputElement) variantInput.value = variantId;
  };

  /**
   * Handles mobile Quick Add button clicks
   * @param {Event} event - The click event
   */
  #handleMobileQuickAddButtonClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const mobileQuickAddButton = event.target.closest('.mobile_quick-add-button');
    if (!(mobileQuickAddButton instanceof HTMLButtonElement) || mobileQuickAddButton.disabled) {
      return;
    }
    if (!mobileQuickAddButton.parentElement) return;
    mobileQuickAddButton.parentElement.classList.toggle('mobile-open');
  };

  /**
   * Handles simple Quick Add button clicks (no size variations)
   * @param {Event} event - The click event
   */
  #handleQuickAddButtonClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const quickAddButton = event.target.closest('.product-card__quick-add-button');
    if (!(quickAddButton instanceof HTMLButtonElement) || quickAddButton.disabled) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const variantId = quickAddButton.getAttribute('data-variant-id');
    if (!variantId) return;

    const originalText = quickAddButton.querySelector('.product-card__quick-add-text')?.textContent || quickAddButton.textContent;

    quickAddButton.disabled = true;
    quickAddButton.classList.add('product-card__quick-add-button--loading');
    quickAddButton.setAttribute('aria-busy', 'true');

    // #handleVariantSelected copies the re-rendered quick-add's dataset over, so this tracks the
    // selected variant rather than the one the card first rendered with.
    const properties = buildQuickAddProperties(event.target, this.dataset.auctionCode);

    // Add to cart via AJAX
    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items: [{
          id: variantId,
          quantity: 1,
          properties: properties
        }]
      })
    })
    .then(async (response) => {
      const data = await response.json().catch(() => ({}));
      const ok = response.ok && !(data && typeof data === 'object' && data.status);
      return { ok, data };
    })
    .then(async ({ ok }) => {
      if (!ok) {
        return;
      }

      this.dispatchEvent(new CartUpdateEvent({}, this.id, {
        action: 'add',
        source: 'quick-add',
        productId: this.dataset.productMasterId,
        productData: {
          masterProductId: this.dataset.productMasterId,
          item_id: this.dataset.productId,
          item_name: this.dataset.productTitle,
          item_brand: this.dataset.productVendor,
          item_category: this.dataset.productCategory,
          item_variant: this.dataset.productVariant,
          price: this.dataset.productPrice,
          quantity: 1,
        }
      }));

      // Keep original button text (e.g. "Quick Add"); do not switch to "Added"
    })
    .catch((error) => {
      console.error('Error adding to cart:', error);
    })
    .finally(() => {
      quickAddButton.classList.remove('product-card__quick-add-button--loading');
      quickAddButton.setAttribute('aria-busy', 'false');
      quickAddButton.disabled = false;
    });
  };

  /**
   * Updates size options when color changes
   * @param {CustomEvent} event - The variant:selected event
   */
  #handleVariantSelected = (event) => {
    const nextProductId =
      event.detail.data.newProduct?.id ||
      event.detail.data.productId ||
      (event.target instanceof HTMLElement ? event.target.dataset.productId : undefined);
    const currentProductId = this.dataset.productMasterId || this.closest('product-card')?.dataset.productId;

    if (event.detail.data.newProduct?.id) {
      this.dataset.productMasterId = String(event.detail.data.newProduct.id);
    } else if (nextProductId && currentProductId && String(nextProductId) !== String(currentProductId)) {
      return;
    }

    const newQuickAdd = event.detail.data.html.querySelector('quick-add');
    const newQuickAddWrapper = newQuickAdd?.querySelector('.product-card__quick-add-wrapper');
    const currentQuickAddWrapper = this.querySelector('.product-card__quick-add-wrapper');

    if (!newQuickAddWrapper || !currentQuickAddWrapper) return;

    if (newQuickAdd instanceof HTMLElement) {
      Object.entries(newQuickAdd.dataset).forEach(([key, value]) => {
        this.dataset[key] = value;
      });
    }

    if (currentQuickAddWrapper.innerHTML !== newQuickAddWrapper.innerHTML) {
      currentQuickAddWrapper.replaceWith(newQuickAddWrapper);
    }
  };

  /**
   * Closes the quick add modal when media query changes
   */
  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Gets the product page URL for modal functionality
   * @returns {string} The product page URL
   */
  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const productLink = productCard?.getProductCardLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  /**
   * Handles quick add button click for modal (legacy support)
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
    }

    this.#openQuickAddModal();
  };

  /**
   * Opens the quick add modal
   */
  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });

    dialogComponent.showDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker in the modal.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add')) {
  customElements.define('quick-add', QuickAdd);
}

/**
 * Document-level handler for "Add to Bag" alternate CTA (when quick add is disabled on CX product slider).
 * Dispatches CartUpdateEvent so header/cart drawer update.
 */
function handleAltCtaAddClick(event) {
  if (!(event.target instanceof Element)) return;
  const btn = event.target.closest('.product-card__alt-cta-add');
  if (!(btn instanceof HTMLButtonElement) || btn.disabled || !btn.hasAttribute('data-variant-id')) {
    return;
  }
  event.preventDefault();
  event.stopPropagation();
  const variantId = btn.getAttribute('data-variant-id');
  if (!variantId) return;

  btn.disabled = true;
  btn.classList.add('product-card__quick-add-button--loading');
  btn.setAttribute('aria-busy', 'true');

  const properties = buildQuickAddProperties(event.target, btn.getAttribute('data-auction-code'));

  fetch(Theme.routes.cart_add_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      items: [{ id: variantId, quantity: 1, properties: properties }]
    })
  })
    .then(async (response) => {
      const data = await response.json().catch(() => ({}));
      const ok = response.ok && !(data && typeof data === 'object' && data.status);
      return { ok, data };
    })
    .then(({ ok }) => {
      if (!ok) return;
      document.dispatchEvent(new CartUpdateEvent({}, 'alt-cta', { action: 'add', source: 'quick-add' }));
      // Keep original button text (e.g. "Quick Add"); do not switch to "Added"
    })
    .catch((err) => console.error('Error adding to cart:', err))
    .finally(() => {
      btn.classList.remove('product-card__quick-add-button--loading');
      btn.setAttribute('aria-busy', 'false');
      if (!btn.classList.contains('product-card__quick-add-button--added')) btn.disabled = false;
    });
}

document.addEventListener('click', handleAltCtaAddClick, true);

// Keep QuickAddDialog for modal functionality
class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
